import { useState } from "react";
import { Clock, PauseCircle, X, Check, Plus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

// Matches SILENCE_THRESHOLD_DAYS in backend/feed.py.
const SILENCE_THRESHOLD_DAYS = 14;

/**
 * The state of a project, above the story of it.
 *
 * Two things live here, and each answers a question the timeline cannot: how
 * long since anybody spoke, and who to ask about it. Neither is a plan or a
 * schedule \u2014 this team discovers the path of a project rather than laying it
 * out in advance.
 */
export function ProjectStatusBar({
  project,
  allUsers,
  onSaveWaiting,
  onTogglePerson,
  s,
}) {
  const [editingWait, setEditingWait] = useState(false);
  const [waitText, setWaitText] = useState(project.waiting_for || "");
  const [saving, setSaving] = useState(false);

  const days = project.silence_days;
  const alarming =
    typeof days === "number" && days >= SILENCE_THRESHOLD_DAYS && project.status !== "closed";
  const people = project.people || [];
  // Both sides normalised to strings. Built from p.id without String() and
  // compared against String(u._id), the tick never appeared where the types
  // differed: the menu showed nobody as already on the project, and clicking a
  // name that was already there silently took them off it.
  const involvedIds = new Set(people.map((p) => String(p.id)));

  const startEdit = () => {
    setWaitText(project.waiting_for || "");
    setEditingWait(true);
  };

  const save = async (clear = false) => {
    setSaving(true);
    try {
      await onSaveWaiting(clear ? "" : waitText.trim());
      setEditingWait(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm"
      data-testid="project-status-bar"
    >
      <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
        {/* Silence. Not a deadline \u2014 this team has none \u2014 but how long nobody
            has said anything, which is how projects die here. */}
        {typeof days === "number" && (
          <div data-testid="project-detail-silence">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              {s("proj.lastActivity")}
            </p>
            <div className="flex items-center gap-1.5">
              <Clock className={`w-4 h-4 ${alarming ? "text-amber-600" : "text-slate-400"}`} />
              <span
                className={`text-sm ${alarming ? "text-amber-700 font-medium" : "text-slate-700"}`}
              >
                {days === 0 ? s("proj.today") : s("proj.silentDays", { days })}
              </span>
            </div>
          </div>
        )}

        {/* Reference people. Descriptive, never a permission: everyone can see
            every project regardless of who is listed. Names rather than bare
            initials, because the point is knowing who to call \u2014 and an initial
            does not tell you that. */}
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
            {s("proj.people")}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {people.map((p) => (
              <span
                key={p.id}
                className="group inline-flex items-center gap-1.5 pl-1 pr-1 py-1 rounded-full bg-slate-50 border border-slate-200 text-sm max-w-full"
                data-testid={`person-chip-${p.id}`}
              >
                <span
                  className="w-5 h-5 rounded-full text-white text-[10px] font-semibold flex items-center justify-center shrink-0"
                  style={{ backgroundColor: p.color || "#3730A3" }}
                >
                  {(p.display_name || "?").charAt(0).toUpperCase()}
                </span>
                <span className="text-slate-700 truncate">{p.display_name}</span>
                {/* 24px rather than the bare icon: this removes somebody from a
                    project and it sat inside a chip, where a thumb hits the
                    chip instead. The label says what it does \u2014 it used to
                    repeat the person's name, which reads as if it selects
                    them. */}
                <button
                  onClick={() => onTogglePerson(p.id)}
                  aria-label={`${s("proj.waitingClear")} \u00b7 ${p.display_name}`}
                  data-testid={`remove-person-${p.id}`}
                  className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="add-person-btn"
                  aria-label={s("proj.people")}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-dashed border-slate-300 text-sm text-slate-400 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {people.length === 0 ? s("proj.noPeople") : ""}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {allUsers.map((u) => (
                  <DropdownMenuItem
                    key={u._id}
                    onClick={() => onTogglePerson(u._id)}
                    data-testid={`toggle-person-${u._id}`}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <span
                      className="w-5 h-5 rounded-full text-white text-[10px] font-semibold flex items-center justify-center shrink-0"
                      style={{ backgroundColor: u.color || "#3730A3" }}
                    >
                      {(u.display_name || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className="flex-1 truncate">{u.display_name}</span>
                    {involvedIds.has(String(u._id)) && (
                      <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* What the project is waiting on. A note, not an assignment: nobody
          here has authority over anybody, so it names the thing being waited
          for and never a person who owes it. */}
      <div className="mt-4 pt-3 border-t border-slate-100">
        {editingWait ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <PauseCircle className="w-4 h-4 text-slate-400 shrink-0 hidden sm:block" />
            <Input
              autoFocus
              value={waitText}
              onChange={(e) => setWaitText(e.target.value)}
              placeholder={s("proj.waitingPlaceholder")}
              data-testid="waiting-input"
              className="flex-1 h-10 border-slate-200"
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") setEditingWait(false);
              }}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => save()}
                disabled={saving}
                data-testid="waiting-save"
                className="bg-indigo-700 hover:bg-indigo-600 text-white h-10 flex-1 sm:flex-none"
              >
                {s("proj.waitingSet")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditingWait(false)}
                aria-label={s("files.cancel")}
                className="border-slate-200 h-10 w-10 shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : project.waiting_for ? (
          // Wraps: the note is free text and can be a sentence, which on a
          // phone pushed Edit and Clear off the right of the card.
          <div className="flex flex-wrap items-start gap-x-2 gap-y-1" data-testid="waiting-display">
            <PauseCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700 break-words flex-1 min-w-0">
              <span className="text-slate-500">{s("proj.waitingLabel")}:</span> {project.waiting_for}
            </span>
            <span className="flex items-center gap-3 shrink-0">
              <button
                onClick={startEdit}
                data-testid="waiting-edit"
                className="text-xs text-indigo-600 hover:underline py-1"
              >
                {s("common.editShort")}
              </button>
              <button
                onClick={() => save(true)}
                data-testid="waiting-clear"
                className="text-xs text-slate-400 hover:text-red-600 py-1"
              >
                {s("proj.waitingClear")}
              </button>
            </span>
          </div>
        ) : (
          <button
            onClick={startEdit}
            data-testid="waiting-add"
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-indigo-600 transition-colors py-1"
          >
            <PauseCircle className="w-4 h-4" />
            {s("proj.addWaiting")}
          </button>
        )}
      </div>
    </div>
  );
}
